import useViewer from "../viewer-context/useViewer";
import { promisifyJigsawCall } from "../../external/utils";

export enum HighlightEvent {
  CREATE = "highlight:created",
  DELETE = "highlight:deleted",
  UPDATE = "highlight:updated",
}

/**
 * This exists in order to support external calls into the reader, such as
 * might occur in an integration that includes an external notebook panel.
 *
 * Return values from create/delete/update are intentionally omitted and changed
 * to events because the UI will need to respond to the results of external calls
 */
export default function useHighlightApi() {
  const viewer = useViewer().viewer as jigsaw.Viewer;

  function getHighlights() {
    return promisifyJigsawCall(viewer.Book.getHighlights.bind(viewer.Book));
  }

  function deleteHighlight(syncId: string): void {
    promisifyJigsawCall(viewer.Book.deleteHighlight.bind(viewer.Book), [
      syncId,
    ]).then(() => {
      document.dispatchEvent(
        new CustomEvent(HighlightEvent.DELETE, { detail: { syncId } })
      );
    });
  }

  function updateHighlight(
    id: string,
    data: { markerId?: string; noteText?: string }
  ): void {
    promisifyJigsawCall(viewer.Book.updateHighlight.bind(viewer.Book), [
      id,
      data,
    ]).then((resp: crane.ResponseData) => {
      document.dispatchEvent(
        new CustomEvent(HighlightEvent.UPDATE, { detail: resp })
      );
    });
  }

  function highlightCurrentSelection(data: {
    markerId: string;
    noteText?: string;
  }): void {
    promisifyJigsawCall(
      viewer.Book.highlightCurrentSelection.bind(viewer.Book),
      [data]
    ).then((resp: crane.ResponseData) => {
      document.dispatchEvent(
        new CustomEvent(HighlightEvent.CREATE, { detail: resp })
      );
    });
  }

  return {
    getHighlights,
    deleteHighlight,
    updateHighlight,
    highlightCurrentSelection,
  };
}
